import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface VehicleType {
  id: string;
  name: string;
  category: string;
  image_url?: string;
  default_capacity?: number;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function useVehicleTypes() {
  return useQuery({
    queryKey: ['vehicle-types'],
    queryFn: async (): Promise<VehicleType[]> => {
      const { data, error } = await supabase
        .from('fleet_vehicle_types')
        .select('*')
        .order('name');
      
      if (error) throw error;
      return (data || []) as VehicleType[];
    }
  });
}

export function useActiveVehicleTypes() {
  return useQuery({
    queryKey: ['vehicle-types', 'active'],
    queryFn: async (): Promise<VehicleType[]> => {
      const { data, error } = await supabase
        .from('fleet_vehicle_types')
        .select('*')
        .eq('is_active', true)
        .order('name');
      
      if (error) throw error;
      return (data || []) as VehicleType[];
    }
  });
}

export function useCreateVehicleType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vehicleType: Omit<VehicleType, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('fleet_vehicle_types')
        .insert([vehicleType])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-types'] });
      toast.success('Tipo de veículo criado com sucesso');
    },
    onError: (error: any) => {
      console.error('Erro ao criar tipo de veículo:', error);
      toast.error(error.message || 'Erro ao criar tipo de veículo');
    }
  });
}

export function useUpdateVehicleType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<VehicleType> & { id: string }) => {
      const { data, error } = await supabase
        .from('fleet_vehicle_types')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-types'] });
      toast.success('Tipo de veículo atualizado com sucesso');
    },
    onError: (error: any) => {
      console.error('Erro ao atualizar tipo de veículo:', error);
      toast.error(error.message || 'Erro ao atualizar tipo de veículo');
    }
  });
}

export function useDeleteVehicleType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('fleet_vehicle_types')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-types'] });
      toast.success('Tipo de veículo excluído com sucesso');
    },
    onError: (error: any) => {
      console.error('Erro ao excluir tipo de veículo:', error);
      toast.error(error.message || 'Erro ao excluir tipo de veículo');
    }
  });
}
